import {
  useCompanyInvitesQuery,
  useCompanyMembersQuery,
  useInviteCompanyMemberMutation,
  useRespondToPermissionUpgradeRequestMutation,
} from "@/api/companyApi";
import CompanyLogoPlaceholder from "@/assets/light_gradient/company_logo_placeholder.svg";
import PermissionGuideIcon from "@/assets/profile/687FFF_user_guide.svg";
import Loader from "@/components/Common/kpi/Loader";
import Modal from "@/components/Common/Modal";
import CompanyMembers from "@/components/MultiAccount/CompanyMembers";
import InviteMember from "@/components/MultiAccount/InviteMember";
import PermissionGuide from "@/components/MultiAccount/PermissionGuide";
import {useIsAdmin, useSelectedCompany} from "@/hooks/useUserHooks";
import ProfileSeparator from "@/pages/profile/ProfileSeparator";
import useCompanyStore from "@/store/company";
import {inviteStateAtom, multiAccountInviteModalAtom} from "@/store/jotaiAtoms";
import {useAtom} from "jotai";
import {RESET} from "jotai/utils";
import {useMemo, useState, type FC} from "react";
import {useTranslation} from "react-i18next";
import {toast} from "react-toastify";

const InviteModal: FC = () => {
  const {t} = useTranslation(["multi_account", "common"]);
  const [inviteModal, setInviteModal] = useAtom(multiAccountInviteModalAtom);
  const isAdmin = useIsAdmin();
  const companyFromStore = useCompanyStore(state => state.company);
  const selectedCompany = useSelectedCompany();
  const company = useMemo(
    () => (isAdmin && !inviteModal.isViewingOwnCompany ? companyFromStore : selectedCompany),
    [isAdmin, companyFromStore, selectedCompany, inviteModal.isViewingOwnCompany],
  );
  const [invite, setInvite] = useAtom(inviteStateAtom);
  const [showPermissionGuide, setShowPermissionGuide] = useState(false);

  const {data: companyMembers, isLoading: isLoadingCompanyMembers} = useCompanyMembersQuery({
    companyId: company?.id!,
    queryOptions: {
      refetchOnMount: true,
    },
  });
  const {data: companyInvites, isLoading: isLoadingCompanyInvites} = useCompanyInvitesQuery({
    companyId: company?.id!,
    queryOptions: {
      refetchOnMount: true,
    },
  });

  const {mutate: inviteMember} = useInviteCompanyMemberMutation({
    companyId: company?.id!,
    email: invite.email,
    selectedPermission: invite.permission,
    onSuccessCallback: () => {
      toast.success(t("invitation_success", {email: invite.email}));
      setInvite(RESET);
    },
  });

  const {mutate: upgradePermission} = useRespondToPermissionUpgradeRequestMutation({companyId: company?.id!});

  return (
    <Modal
      onClose={() =>
        setInviteModal({
          isVisible: false,
          isViewingOwnCompany: false,
        })
      }
      modalTitle={t("invite_modal_title")}
      className="min-h-[480px]"
      backgroundClassName="flex max-w-[600px]"
      customBottomRow={<></>}>
      <div className="flex flex-col gap-5">
        <InviteMember
          logo={company?.picture320 ?? company?.picture ?? CompanyLogoPlaceholder}
          name={company?.company_name!}
          inviteMember={inviteMember}
        />
        {isLoadingCompanyMembers || isLoadingCompanyInvites ? (
          <Loader />
        ) : (
          <CompanyMembers
            companyId={company?.id!}
            companyMembers={companyMembers ?? []}
            companyInvites={companyInvites?.filter(invite => invite.status === "PENDING") ?? []}
            upgradePermission={memberId => upgradePermission({user_id: memberId, status: "REJECTED"})}
          />
        )}
        <ProfileSeparator
          text={t("multi_account:permission_guide")}
          icon={PermissionGuideIcon}
          isToggleable
          isToggled={showPermissionGuide}
          onToggle={() => setShowPermissionGuide(!showPermissionGuide)}
        />
        {showPermissionGuide && <PermissionGuide />}
        <div className="h-10" />
      </div>
    </Modal>
  );
};

export default InviteModal;
